import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { WEB_COMPONENT_GZ_GENERIC_WIDGET } from '../../../webcomponents.constants';
import { styles } from '../view/styles';
import { AlignTitle, WidgetEvents, WidgetStateFrontEnd } from '../types';
import { OverlayPositionX } from '../../../ui/gzSelect/types';
import { translateFn } from '../../../utils/translator.helper';
import { ContentOrientation } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { GzGenericWidgetContent } from './GzGenericWidgetContent';

/**
 * @summary Component that can be used in order to create widgets.
 * @tag gz-generic-widget
 */
@customElement(WEB_COMPONENT_GZ_GENERIC_WIDGET)
export class GzGenericWidget extends LitElement {
  /**
   * @type {string}
   * The title of the widget.
   */
  @property({ type: String, attribute: true, reflect: true })
  widgetTitle = '';

  /**
   * @type {WidgetStateFrontEnd}
   * Sets the state of the widget. It has the following values: <strong>loading, active, empty and error</strong>.
   * Default is <strong>loading</strong>.
   */
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state = WidgetStateFrontEnd.IS_LOADING;

  /**
   * @type {boolean}
   * Set the closable property of a widget. If <strong>true</strong> the widget will have a close icon button in the top
   * right of the screen. If clicked a new '<strong>removed</strong>' event will be fired.
   * Default is <strong>false</strong>.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  closable = false;

  /**
   * @type {boolean}
   * Set the resizable property of a widget. If <strong>true</strong> the widget will have a resize icon button in the
   * bottom right of the widget visible on hover.
   * Default is <strong>false</strong>.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  resizable = false;

  /**
   * @type {boolean}
   * Set the movable property of a widget. If <strong>true</strong> the widget will have a drag icon button in the top
   * left of the widget visible on hover.
   * Default if <strong>false</strong>.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  movable = false;

  /**
   * @type {boolean}
   * Set if the title of the widget will be clickable. If <strong>true</strong> the title will be clickable and a new
   * '<strong>titleClicked</strong>' event will be fired.
   * Default is <strong>false</strong>.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  titleHasAction = false;

  /**
   * @type {Array<SelectOption>}
   * Sets the options for the actions button. Default is <strong>undefined</strong>. If undefined the actions button
   * will not be displayed.
   */
  @property({ type: Array, attribute: true, reflect: true })
  actions = undefined;

  @property({ type: Boolean, attribute: true, reflect: true })
  hideActionsOnLoading = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  hideTitle = false;

  @property({ type: Object, attribute: true, reflect: true })
  dependencies;

  @property({ type: String, attribute: true, reflect: true })
  alignTitle = AlignTitle.LEFT;

  /**
   * @type {string: ContentOrientation}
   * Sets the direction for the widget content items (i.e. header, content, footer):
   * Vertical (default) displays the items stacked in a column
   * Horizontal displays the items in a row
   */
  @property({ type: String, attribute: true, reflect: true })
  contentOrientation: ContentOrientation = ContentOrientation.VERTICAL;

  @property({ type: String, attribute: true, reflect: true })
  widgetContentStyle?;

  static get styles() {
    return styles;
  }

  firstUpdated() {
    this.addEventListener(WidgetEvents.LOADED_COMPONENT, this.handleWidgetLoaded);
  }

  handleWidgetLoaded() {
    const widgetContent = this.shadowRoot
      ?.querySelector('slot')
      ?.assignedNodes()
      .find(node => node instanceof GzGenericWidgetContent) as GzGenericWidgetContent;
    widgetContent.injectDependencies(this.dependencies);
  }

  render() {
    return html`
      <div class="main-container">
        ${this.renderMoveIcon()}
        <div class="widget-header">${this.renderTitle()} ${this.renderErrorIcon()} ${this.renderCloseIcon()}</div>
        <div class="widget-content" style=${this.widgetContentStyle}>
          <slot></slot>
        </div>
        <div class="widget-footer">${this.renderActionsButtons()}</div>
      </div>
    `;
  }

  public injectDependencies(dependencies: any): void {
    this.dependencies = dependencies;
  }

  private renderTitle() {
    let titleClass = 'title';

    if (this.alignTitle === AlignTitle.CENTER) {
      titleClass = 'title-center';
    }

    return !this.hideTitle
      ? html`<gz-tooltip isEllipsis tooltipText="${this.widgetTitle}" class=${titleClass}>
          <gz-title
            @click="${this.titleHasAction ? this.handleClickTitle : () => {}}"
            class="${this.titleHasAction && this.state === WidgetStateFrontEnd.IS_ACTIVE
              ? 'widget-title has-action'
              : 'widget-title'}"
            fontSize="16"
          >
            ${this.widgetTitle}
          </gz-title></gz-tooltip
        >`
      : nothing;
  }

  private renderErrorIcon() {
    const tooltipError = translateFn(
      'An internal error prevented the widget from displaying data. Please contact Bitdefender Enterprise Support for more information.'
    );
    return this.state === WidgetStateFrontEnd.IS_ERROR && !this.hideTitle
      ? html`<gz-tooltip tooltipText="${tooltipError}" class="error-tooltip">
          <gz-icon size="24" name="ds-icon-notification_negative" color="var(--red-layout-300-primary)"></gz-icon>
        </gz-tooltip>`
      : nothing;
  }

  private renderMoveIcon() {
    return this.movable && !this.hideActionsOnLoading
      ? html`<gz-icon-button
          id="move-icon"
          @mousedown="${this.handleStartDragging}"
          buttonColor="secondary"
          buttonType="square"
          icon="ds-icon-drag"
          part="move-icon"
          fontSize="24"
        ></gz-icon-button>`
      : nothing;
  }

  private renderCloseIcon() {
    return this.closable && !this.hideActionsOnLoading
      ? html`<gz-icon-button
          @click="${this.handleRemoveWidget}"
          id="close-button"
          buttonColor="secondary"
          buttonType="square"
          icon="ds-icon-clear"
          part="close-button"
          fontSize="24"
        >
        </gz-icon-button>`
      : nothing;
  }

  private renderActionsButtons() {
    return this.actions
      ? html`<gz-select
          @selectionChanged="${this.handleSelectionChanged}"
          class="actions-select"
          ?disabled="${this.state !== WidgetStateFrontEnd.IS_ACTIVE}"
          options="${JSON.stringify(this.actions)}"
          overlaypositionx="${OverlayPositionX.LEFT}"
        >
          <gz-button class="action-button">
            <span data-actions class="action-button-text">${translateFn('ACTIONS')}</span>
            <span class="action-icon ds-icon-dropdown_arrow"></span>
          </gz-button>
        </gz-select>`
      : nothing;
  }

  private handleSelectionChanged(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent(WidgetEvents.ACTION_CLICKED, {
        detail: { selectedValue: event.detail.selected },
        bubbles: true,
        composed: true,
      })
    );
    event.stopPropagation();
  }

  private handleRemoveWidget() {
    this.dispatchEvent(new CustomEvent(WidgetEvents.REMOVED, { bubbles: true, composed: true }));
  }

  private handleClickTitle() {
    const widgetContent = this.shadowRoot?.querySelector('slot')?.assignedElements()[0] as GzGenericWidgetContent;
    if (this.state === WidgetStateFrontEnd.IS_ACTIVE) {
      widgetContent.handleTitleClick();
    }
  }

  private handleStartDragging() {
    this.dispatchEvent(new CustomEvent(WidgetEvents.START_DRAG, { bubbles: true, composed: true }));
  }
}
